import ApiClient from "../client";
import { PaginatedResponse } from "../types";
import {
  ApiRouteDetail,
  ApiGradesResponse,
  ApiGradesResponseRaw,
  ClimbType,
  ApiRoutePayload,
  ApiBulkRoutePatch,
  ApiCircuit,
  ApiLine,
  ApiRouteAttempt,
  ApiCreateZonePayload,
  ApiZone,
  ApiUpdateZonePayload,
  ApiCreateLinePayload,
  ApiUpdateLinePayload,
  ApiRouteCustomScoringConfig,
  ApiRoutesTargetsResponse,
  ApiBulkRoutesTargetPatch,
  ApiRouteLabelConfig,
  ApiRouteLabelGroupInfo,
  ApiRouteLabel,
  ApiCreateRouteLabelGroupPayload,
  ApiUpdateRouteLabelGroupPayload,
  ApiCreateRouteLabelPayload,
  ApiUpdateRouteLabelPayload,
  ApiBulkRouteLabelGroupPatch,
  ApiBulkRouteLabelPatch,
  ApiSubmittedRouteComment,
} from "../types/routes";

export const getRoute = (client: ApiClient, uuid: string) =>
  client.get<ApiRouteDetail>(`/routes/${uuid}`);

export const updateRoute = (
  client: ApiClient,
  uuid: string,
  data: Partial<ApiRoutePayload>
) => client.patch<ApiRouteDetail>(`/routes/${uuid}`, data);

export const createRoute = (
  client: ApiClient,
  data: Partial<ApiRoutePayload> & { venue: string; climbType: ClimbType }
) => client.post<ApiRouteDetail>(`/routes`, data);

export const updateRoutes = (client: ApiClient, updates: ApiBulkRoutePatch[]) =>
  client.patch<ApiRouteDetail[]>(`/routes`, updates);

export const createRouteComment = (
  client: ApiClient,
  routeUuid: string,
  text: string
) =>
  client.post<ApiSubmittedRouteComment>(`/routes/${routeUuid}/comments`, {
    text,
  });

export const getGrades = (client: ApiClient): Promise<ApiGradesResponse> => {
  const result = client.get<ApiGradesResponseRaw>(`/grades`, {
    convertToCamelCase: false,
  });
  return result.then((data) => {
    const { custom_systems, mappings, ...result } = data;
    return {
      customSystems: Object.fromEntries(
        Object.entries(custom_systems).map(([name, system]) => [
          name,
          {
            pinValues: system.pin_values,
            label: system.label,
          },
        ])
      ),
      mappings,
      ...result,
    } as ApiGradesResponse;
  });
};

export const getCircuit = (client: ApiClient, uuid: string) =>
  client.get<ApiCircuit>(`/circuits/${uuid}`);

export const getLine = (client: ApiClient, uuid: string) =>
  client.get<ApiLine>(`/lines/${uuid}`);

export const createLine = (client: ApiClient, payload: ApiCreateLinePayload) =>
  client.post<ApiLine>(`/lines`, payload);

export const updateLine = (
  client: ApiClient,
  uuid: string,
  payload: ApiUpdateLinePayload
) => client.patch<ApiLine>(`/lines/${uuid}`, payload);

export const deleteLine = (client: ApiClient, uuid: string) =>
  client.delete(`/lines/${uuid}`);

export const getAllRouteAttempts = (
  client: ApiClient,
  routeUuid: string,
  competitionOnly?: boolean
) =>
  client.get<PaginatedResponse<ApiRouteAttempt & { user: string }>>(
    `/routes/${routeUuid}/attempts`,
    {
      searchParams: {
        ...(competitionOnly ? { is_competition: "1" } : {}),
        user: "ALL",
      },
    }
  );

export const createZone = (
  client: ApiClient,
  venueUuid: string,
  data: ApiCreateZonePayload
) => client.post<ApiZone>(`/venues/${venueUuid}/zones`, data);

export const updateZone = (
  client: ApiClient,
  venueUuid: string,
  uuid: string,
  data: ApiUpdateZonePayload
) => client.patch<ApiZone>(`/venues/${venueUuid}/zones/${uuid}`, data);

export const deleteZone = (
  client: ApiClient,
  venueUuid: string,
  uuid: string
) => client.delete(`/venues/${venueUuid}/zones/${uuid}`);

export const getVenueRouteLabels = (
  client: ApiClient,
  venueUuid: string,
  { includeArchived }: { includeArchived?: boolean } | undefined = {}
) =>
  client.get<ApiRouteLabelConfig>(`/venues/${venueUuid}/route-labels`, {
    searchParams: includeArchived ? { include_archived: true } : undefined,
  });

export const createRouteLabel = (
  client: ApiClient,
  venueUuid: string,
  data: ApiCreateRouteLabelPayload
) => client.post<ApiRouteLabel>(`/venues/${venueUuid}/route-labels`, data);

export const updateRouteLabel = (
  client: ApiClient,
  venueUuid: string,
  uuid: string,
  data: ApiUpdateRouteLabelPayload
) =>
  client.patch<ApiRouteLabel>(`/venues/${venueUuid}/route-labels/${uuid}`, data);

// Reordering is saved by sending new `orderIndex` values for the siblings.
export const bulkUpdateRouteLabels = (
  client: ApiClient,
  venueUuid: string,
  updates: ApiBulkRouteLabelPatch[]
) => client.patch<ApiRouteLabel[]>(`/venues/${venueUuid}/route-labels`, updates);

// Deletes the label for good. The server answers 409 if a route has it, and
// the label should be archived instead by patching `isActive` to false.
export const deleteRouteLabel = (
  client: ApiClient,
  venueUuid: string,
  uuid: string
) => client.delete(`/venues/${venueUuid}/route-labels/${uuid}`);

export const createRouteLabelGroup = (
  client: ApiClient,
  venueUuid: string,
  data: ApiCreateRouteLabelGroupPayload
) =>
  client.post<ApiRouteLabelGroupInfo>(
    `/venues/${venueUuid}/route-labels/groups`,
    data
  );

export const updateRouteLabelGroup = (
  client: ApiClient,
  venueUuid: string,
  uuid: string,
  data: ApiUpdateRouteLabelGroupPayload
) =>
  client.patch<ApiRouteLabelGroupInfo>(
    `/venues/${venueUuid}/route-labels/groups/${uuid}`,
    data
  );

export const bulkUpdateRouteLabelGroups = (
  client: ApiClient,
  venueUuid: string,
  updates: ApiBulkRouteLabelGroupPatch[]
) =>
  client.patch<ApiRouteLabelGroupInfo[]>(
    `/venues/${venueUuid}/route-labels/groups`,
    updates
  );

// Deletes the group and its labels. Refused with a 409 like `deleteRouteLabel`
// if a route has any of its labels, archived ones included.
export const deleteRouteLabelGroup = (
  client: ApiClient,
  venueUuid: string,
  uuid: string
) => client.delete(`/venues/${venueUuid}/route-labels/groups/${uuid}`);

export const getRouteCustomScoringConfig = (
  client: ApiClient,
  routeUuid: string
) =>
  client.get<ApiRouteCustomScoringConfig>(
    `/routes/${routeUuid}/custom-scoring-config`
  );

export const updateRouteCustomScoringConfig = (
  client: ApiClient,
  routeUuid: string,
  data: ApiRouteCustomScoringConfig
) =>
  client.patch<ApiRouteCustomScoringConfig>(
    `/routes/${routeUuid}/custom-scoring-config`,
    data
  );

export const getRoutesTargets = (
  client: ApiClient,
  venueUuid: string,
  climbType: ClimbType
) =>
  client.get<ApiRoutesTargetsResponse>(`/venues/${venueUuid}/routes/targets`, {
    searchParams: { climb_type: climbType },
  });

export const generateRoutesTargets = (
  client: ApiClient,
  venueUuid: string,
  climbType: ClimbType,
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE"
) =>
  client.post(`/venues/${venueUuid}/routes/targets/generate`, {
    climbType,
    mode,
  });

export const updateRoutesTargets = (
  client: ApiClient,
  updates: ApiBulkRoutesTargetPatch[]
) => client.patch(`/routes/targets`, updates);

export const logQrScan = (client: ApiClient, url: string) =>
  client.post(`/routes/log-qr-scan`, { url });

export const endpoints = {
  getRoute,
  createRoute,
  updateRoute,
  updateRoutes,
  createRouteComment,
  getGrades,
  getCircuit,
  getLine,
  createLine,
  updateLine,
  deleteLine,
  getAllRouteAttempts,
  createZone,
  updateZone,
  deleteZone,
  getVenueRouteLabels,
  createRouteLabel,
  updateRouteLabel,
  bulkUpdateRouteLabels,
  deleteRouteLabel,
  createRouteLabelGroup,
  updateRouteLabelGroup,
  bulkUpdateRouteLabelGroups,
  deleteRouteLabelGroup,
  getRouteCustomScoringConfig,
  updateRouteCustomScoringConfig,
  getRoutesTargets,
  generateRoutesTargets,
  updateRoutesTargets,
  logQrScan,
};
